// @core/stores/subscription/SubscriptionBundleStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { subscriptionApi } from '@core/api';
import type {
  BundleSubscriptionResponse,
  CreateProductBundleRequest,
  GetBundleSubscriptionPreviewRequest,
  GetBundleSubscriptionPreviewResponse,
  ListBundleSubscriptionsResponse,
  ProductBundle,
  SubscribeToBundleRequest,
  UpdateProductBundleRequest,
} from '@core/types/subscription';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class SubscriptionBundleStore {
  // List state
  items: ProductBundle[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: ProductBundle | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, ProductBundle>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): ProductBundle | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: ProductBundle | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Cancel bundle subscription
   */
  async CancelBundleSubscription(subscription_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.CancelBundleSubscription(subscription_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a product bundle
   */
  async CreateProductBundle(data: CreateProductBundleRequest): Promise<ProductBundle> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.CreateProductBundle(data);
      const item = response.bundle;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Archive product bundle
   */
  async DeleteProductBundle(bundle_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await subscriptionApi.DeleteProductBundle(bundle_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== bundle_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === bundle_id) {
          this.currentItem = null;
        }

        this.removeFromCache(bundle_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get bundle subscription details
   */
  async GetBundleSubscription(subscription_id: string): Promise<BundleSubscriptionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.GetBundleSubscription(subscription_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Preview bundle subscription pricing
   */
  async GetBundleSubscriptionPreview(bundle_id: string, data: GetBundleSubscriptionPreviewRequest): Promise<GetBundleSubscriptionPreviewResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.GetBundleSubscriptionPreview(bundle_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get product bundle details
   */
  async GetProductBundle(bundle_id: string): Promise<ProductBundle> {
    // Check cache first
    const cached = this.cache.get(bundle_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await subscriptionApi.GetProductBundle(bundle_id);
      const item = response.bundle;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List tenant's bundle subscriptions
   */
  async ListBundleSubscriptions(tenant_id: string, params?: PaginatedRequest): Promise<ListBundleSubscriptionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.ListBundleSubscriptions(tenant_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List product bundles
   */
  async ListProductBundles(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await subscriptionApi.ListProductBundles(params);

      runInAction(() => {
        this.items = response.bundles?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Subscribe tenant to product bundle
   */
  async SubscribeToBundle(bundle_id: string, data: SubscribeToBundleRequest): Promise<BundleSubscriptionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.SubscribeToBundle(bundle_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update product bundle
   */
  async UpdateProductBundle(bundle_id: string, data: UpdateProductBundleRequest): Promise<ProductBundle> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.UpdateProductBundle(bundle_id, data);
      const item = response.bundle;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const subscriptionBundleStore = new SubscriptionBundleStore();
